// Module identity registry.
//
// One row per top-level admin module. Each entry carries the visual identity
// used across all surfaces that reference the module: sidebar nav, launchpad
// tiles, recent-visits chips, settings chooser, and any future module-aware
// surface (breadcrumbs, search results, audit chips, etc.).
//
// SVG paths are sized for a 24×24 viewBox with `currentColor` strokes so a
// single component can render the icon at any size and color the caller asks
// for. Colors are stored as Tailwind gradient class strings so consumers can
// drop them straight into `bg-gradient-to-br ${gradient}`.
//
// Tailwind safelist note: the gradient + tint class strings below are read
// by Tailwind's JIT scanner because they appear as literal strings in this
// source file. If you add new color stops, also confirm Tailwind has emitted
// them on first build.

import React from 'react';

// ─── Module ids ─────────────────────────────────────────────────────────────

export type ModuleId =
  | 'home'
  | 'dashboard'
  | 'bills'
  | 'tenants'
  | 'users'
  | 'platform-users'
  | 'billing-platform'
  | 'my-subscription'
  | 'locations'
  | 'products'
  | 'categories'
  | 'variant-attributes'
  | 'customers'
  | 'loyalty'
  | 'vouchers'
  | 'memberships'
  | 'communications'
  | 'commissions'
  | 'shifts'
  | 'restaurant'
  | 'festive'
  | 'credit-accounts'
  | 'corporate-accounts'
  | 'exchange-rates'
  | 'staff-performance'
  | 'discounts'
  | 'expenses'
  | 'quotations'
  | 'sales-orders'
  | 'sales-invoices'
  | 'approvals'
  | 'inventory'
  | 'reports'
  | 'notifications'
  | 'audit'
  | 'sync-health'
  | 'backups'
  | 'data-export'
  | 'dead-letters'
  | 'system-health'
  | 'settings';

// ─── Palette ────────────────────────────────────────────────────────────────
// Stored as named constants so multiple modules can share a gradient without
// duplicating the literal string. Tailwind JIT picks up the literals from the
// const declarations.

const G_BRAND = 'from-brand-500 to-indigo-500';        // overview / sales
const G_INDIGO = 'from-indigo-500 to-violet-500';      // platform / admin
const G_VIOLET = 'from-violet-500 to-pink-500';        // restaurant / engagement
const G_PINK = 'from-pink-500 to-danger-500';          // CRM / loyalty
const G_TEAL = 'from-teal-500 to-success-500';         // inventory / catalog
const G_SUCCESS = 'from-success-500 to-teal-500';      // money in / billing
const G_WARN = 'from-warning-500 to-danger-500';       // expenses / alerts
const G_INFO = 'from-info-500 to-brand-500';           // people / health
const G_OPS = 'from-neutral-500 to-indigo-500';        // governance / infra
const G_HEAT = 'from-danger-500 to-warning-500';       // notifications / urgent

// Faint background tint matching each gradient. Used for the tile body so
// adjacent tiles read as colored cards rather than uniform white.
const T_BRAND = 'bg-brand-50/50 dark:bg-brand-900/10';
const T_INDIGO = 'bg-indigo-50/50 dark:bg-indigo-900/10';
const T_VIOLET = 'bg-violet-50/50 dark:bg-violet-900/10';
const T_PINK = 'bg-pink-50/50 dark:bg-pink-900/10';
const T_TEAL = 'bg-teal-50/50 dark:bg-teal-900/10';
const T_SUCCESS = 'bg-success-50/50 dark:bg-success-900/10';
const T_WARN = 'bg-warning-50/50 dark:bg-warning-900/10';
const T_INFO = 'bg-info-50/50 dark:bg-info-900/10';
const T_OPS = 'bg-neutral-100/60 dark:bg-neutral-900/40';
const T_HEAT = 'bg-danger-50/50 dark:bg-danger-900/10';

const TINT_BY_GRADIENT: Record<string, string> = {
  [G_BRAND]: T_BRAND,
  [G_INDIGO]: T_INDIGO,
  [G_VIOLET]: T_VIOLET,
  [G_PINK]: T_PINK,
  [G_TEAL]: T_TEAL,
  [G_SUCCESS]: T_SUCCESS,
  [G_WARN]: T_WARN,
  [G_INFO]: T_INFO,
  [G_OPS]: T_OPS,
  [G_HEAT]: T_HEAT,
};

// ─── Definitions ────────────────────────────────────────────────────────────

interface ModuleIconDef {
  /** One or more SVG path `d` strings, drawn in order with currentColor strokes. */
  paths: string[];
  /** Tailwind gradient class string: `from-X to-Y`. */
  gradient: string;
}

export const MODULE_ICONS: Record<ModuleId, ModuleIconDef> = {
  home: {
    paths: ['M3 12l9-9 9 9M5 10v10a1 1 0 001 1h3v-6h6v6h3a1 1 0 001-1V10'],
    gradient: G_BRAND,
  },
  dashboard: {
    paths: [
      'M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-4 0h4',
    ],
    gradient: G_INDIGO,
  },
  bills: {
    paths: [
      'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z',
    ],
    gradient: G_BRAND,
  },
  tenants: {
    paths: [
      'M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4',
    ],
    gradient: G_INDIGO,
  },
  users: {
    paths: [
      'M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z',
    ],
    gradient: G_INFO,
  },
  'platform-users': {
    paths: [
      'M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z',
    ],
    gradient: G_INDIGO,
  },
  'billing-platform': {
    paths: [
      'M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z',
    ],
    gradient: G_SUCCESS,
  },
  'my-subscription': {
    paths: [
      'M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z',
    ],
    gradient: G_SUCCESS,
  },
  locations: {
    paths: ['M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z'],
    gradient: G_PINK,
  },
  products: {
    paths: ['M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4'],
    gradient: G_TEAL,
  },
  categories: {
    paths: [
      'M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10',
    ],
    gradient: G_TEAL,
  },
  'variant-attributes': {
    paths: [
      'M7 7h.01M7 3h5a1.99 1.99 0 011.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z',
    ],
    gradient: G_VIOLET,
  },
  customers: {
    paths: [
      'M17 20h5v-2a4 4 0 00-3-3.87M9 20H4v-2a4 4 0 013-3.87m3-6a4 4 0 11-8 0 4 4 0 018 0zm6 0a4 4 0 11-8 0 4 4 0 018 0z',
    ],
    gradient: G_PINK,
  },
  loyalty: {
    paths: ['M12 2l2.39 7.36H22l-6.19 4.5 2.39 7.36L12 16.72 5.8 21.22l2.39-7.36L2 9.36h7.61z'],
    gradient: G_WARN,
  },
  vouchers: {
    paths: [
      'M7 7h.01M7 3h5a2 2 0 011.414.586l7 7a2 2 0 010 2.828l-5 5a2 2 0 01-2.828 0l-7-7A2 2 0 013 12V7a4 4 0 014-4z',
    ],
    gradient: G_PINK,
  },
  memberships: {
    paths: ['M5 3l3.5 4.5L12 3l3.5 4.5L19 3v13a2 2 0 01-2 2H7a2 2 0 01-2-2V3z'],
    gradient: G_VIOLET,
  },
  communications: {
    paths: [
      'M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z',
    ],
    gradient: G_VIOLET,
  },
  commissions: {
    paths: [
      'M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z',
    ],
    gradient: G_SUCCESS,
  },
  shifts: {
    paths: ['M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z'],
    gradient: G_INFO,
  },
  restaurant: {
    paths: ['M3 3h18v18H3V3zm0 6h18M9 9v12'],
    gradient: G_VIOLET,
  },
  festive: {
    paths: [
      'M20 12v10H4V12m16 0L12 2 4 12m8-10v10m-8 0h16M12 7H7.5a2.5 2.5 0 010-5C11 2 12 7 12 7zm0 0h4.5a2.5 2.5 0 000-5C13 2 12 7 12 7z',
    ],
    gradient: G_VIOLET,
  },
  'credit-accounts': {
    paths: [
      'M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z',
    ],
    gradient: G_WARN,
  },
  'corporate-accounts': {
    paths: [
      'M3 21h18M3 10h18M5 6l7-3 7 3M4 10v11M20 10v11M8 14v.01M12 14v.01M16 14v.01M8 18v.01M12 18v.01M16 18v.01',
    ],
    gradient: G_INDIGO,
  },
  'exchange-rates': {
    paths: ['M7 16V4m0 0L3 8m4-4l4 4M17 8v12m0 0l4-4m-4 4l-4-4'],
    gradient: G_WARN,
  },
  'staff-performance': {
    paths: ['M16 8v8m-4-5v5m-4-2v2m-2 4h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z'],
    gradient: G_INFO,
  },
  discounts: {
    paths: [
      'M7 7h.01M7 3h5a2 2 0 011.414.586l7 7a2 2 0 010 2.828l-5 5a2 2 0 01-2.828 0l-7-7A2 2 0 013 12V7a4 4 0 014-4z',
    ],
    gradient: G_PINK,
  },
  expenses: {
    paths: [
      'M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z',
    ],
    gradient: G_WARN,
  },
  quotations: {
    paths: [
      'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z',
    ],
    gradient: G_SUCCESS,
  },
  'sales-orders': {
    // Clipboard with a check — a confirmed customer order.
    paths: [
      'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4',
    ],
    gradient: G_BRAND,
  },
  'sales-invoices': {
    // Receipt with a tick — money owed to us for goods sold on sales orders.
    paths: ['M9 14l6-6m-5.5.5h.01m4.99 5h.01M19 21l-3-3-3 3-3-3-3 3V5a2 2 0 012-2h10a2 2 0 012 2v16z'],
    gradient: G_SUCCESS,
  },
  approvals: {
    paths: ['M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z'],
    gradient: G_SUCCESS,
  },
  inventory: {
    paths: ['M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4'],
    gradient: G_TEAL,
  },
  reports: {
    paths: [
      'M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z',
    ],
    gradient: G_BRAND,
  },
  notifications: {
    paths: ['M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9M13.73 21a2 2 0 01-3.46 0'],
    gradient: G_HEAT,
  },
  audit: {
    paths: ['M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z'],
    gradient: G_OPS,
  },
  'sync-health': {
    paths: [
      'M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.582m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15',
    ],
    gradient: G_INFO,
  },
  backups: {
    paths: ['M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4'],
    gradient: G_OPS,
  },
  'data-export': {
    paths: ['M20 16.2A4.5 4.5 0 0017.5 8H17a6 6 0 10-11.4 3M8 17l4 4 4-4M12 12v9'],
    gradient: G_OPS,
  },
  'dead-letters': {
    paths: ['M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0zM12 9v4m0 4h.01'],
    gradient: G_HEAT,
  },
  'system-health': {
    paths: ['M9 3H5a2 2 0 00-2 2v4m6-6h10a2 2 0 012 2v4M9 3v18m0 0h10a2 2 0 002-2V9M9 21H5a2 2 0 01-2-2V9m0 0h18'],
    gradient: G_OPS,
  },
  settings: {
    paths: [
      'M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z',
    ],
    gradient: G_INDIGO,
  },
};

// ─── Public API ─────────────────────────────────────────────────────────────

/**
 * Render the registered SVG icon for `id` at the requested size. Strokes use
 * `currentColor` so wrap in a container with `text-white` (etc.) to color.
 */
export function ModuleIcon({
  id,
  className = 'h-5 w-5',
  strokeWidth = 1.5,
}: {
  id: ModuleId;
  className?: string;
  strokeWidth?: number;
}) {
  const def = MODULE_ICONS[id];
  if (!def) return null;
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      aria-hidden="true"
    >
      {def.paths.map((d, i) => (
        <path key={i} strokeLinecap="round" strokeLinejoin="round" d={d} />
      ))}
    </svg>
  );
}

/** Tailwind `from-X to-Y` for the module. Safe for unknown ids (falls back). */
export function moduleGradient(id: string): string {
  return (MODULE_ICONS as Record<string, ModuleIconDef>)[id]?.gradient ?? G_BRAND;
}

/** Faint background tint matching the module's gradient. */
export function moduleTint(id: string): string {
  return TINT_BY_GRADIENT[moduleGradient(id)] ?? T_BRAND;
}

// ─── Tailwind safelist crumbs ───────────────────────────────────────────────
// The literal class strings above are picked up by the JIT scanner from the
// `const` declarations. Keep this comment block so the gradients and tints
// stay grep-able in source.
//
// gradients: from-brand-500 to-indigo-500 from-indigo-500 to-violet-500
//   from-violet-500 to-pink-500 from-pink-500 to-danger-500
//   from-teal-500 to-success-500 from-success-500 to-teal-500
//   from-warning-500 to-danger-500 from-info-500 to-brand-500
//   from-neutral-500 to-indigo-500 from-danger-500 to-warning-500
//
// tints: bg-brand-50/50 bg-indigo-50/50 bg-violet-50/50 bg-pink-50/50
//   bg-teal-50/50 bg-success-50/50 bg-warning-50/50 bg-info-50/50
//   bg-neutral-100/60 bg-danger-50/50
//   dark:bg-brand-900/10 dark:bg-indigo-900/10 dark:bg-violet-900/10
//   dark:bg-pink-900/10 dark:bg-teal-900/10 dark:bg-success-900/10
//   dark:bg-warning-900/10 dark:bg-info-900/10 dark:bg-neutral-900/40
//   dark:bg-danger-900/10
